import type { WywolanieSzczegolDto } from "@shared/wywolaniaTypy.js";
import { Link, useLocation, useParams } from "react-router-dom";
import { TrescWywolania } from "../components/developer/TrescWywolania.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Card } from "../components/ui/Card.js";
import { KpiWartosc } from "../components/ui/KpiTile.js";
import { StanBledu, StanLadowania, StanPusty } from "../components/ui/stany.js";
import { useZasob } from "../hooks/useZasob.js";
import { api } from "../lib/api.js";
import {
  etykietaStatusu,
  formatujCzas,
  formatujMs,
  komunikatBleduApi,
  kosztWywolaniaDoPokazania,
  skrocId,
  sprzecznoscStatusu,
} from "../lib/developer.js";
import { etykietaDostawcy, etykietaEtapu, formatujLiczbe } from "../lib/koszty.js";
import { TabelaWywolan } from "../components/developer/TabelaWywolan.js";

/**
 * Szczegol jednego wywolania modelu (/developer/:id). To STRONA, nie szuflada ani okno: dialog w tej aplikacji ma 480 px i
 * nie przewija sie, a prompt z odpowiedzia potrzebuja miejsca. Karty-siostry na washu: metadane, zuzycie i koszt, blad,
 * powiazane proby, parametry zapytania, audio oraz dwie karty z trescia (zapytanie, odpowiedz), zwiniete do klikniecia.
 *
 * Nic tu nie udaje, ze dane sa: brak zuzycia to kreska, a tresc ma stan (zapisana, wygasla zgodnie z polityka, niezapisana,
 * w toku) z powodem.
 */

function Wiersz({ nazwa, children }: { nazwa: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <>
      <dt>{nazwa}</dt>
      <dd>{children}</dd>
    </>
  );
}

function Metadane({ w }: { w: WywolanieSzczegolDto }): React.JSX.Element {
  const podmienionyDostawca = w.dostawcaZamowiony !== null && w.dostawcaZamowiony !== w.dostawca;
  const innyModel = w.modelZamowiony !== null && w.modelZamowiony !== w.model;
  const sprzecznosc = sprzecznoscStatusu(w);
  return (
    <Card tytul="Metadane">
      <dl className="developer-lista">
        <Wiersz nazwa="Etap">{etykietaEtapu(w.zakres)}</Wiersz>
        <Wiersz nazwa="Dostawca">
          {etykietaDostawcy(w.dostawca)}
          {podmienionyDostawca && ` (zamówiony: ${etykietaDostawcy(w.dostawcaZamowiony!)})`}
        </Wiersz>
        <Wiersz nazwa="Model">
          {w.model}
          {innyModel && ` (zamówiony: ${w.modelZamowiony})`}
        </Wiersz>
        <Wiersz nazwa="Status">
          <Badge wariant={etykietaStatusu(w.status).wariant}>{etykietaStatusu(w.status).etykieta}</Badge>
          {w.httpStatus !== null && ` HTTP ${w.httpStatus}`}
          {w.powodZakonczenia && ` - ${w.powodZakonczenia}`}
          {sprzecznosc && (
            <>
              {" "}
              <Badge wariant="warning">{sprzecznosc.etykieta}</Badge> {sprzecznosc.opis} Nie polegaj na samym statusie (znane z danych sprzed
              19.08.2026, opis w docs/developer.md).
            </>
          )}
        </Wiersz>
        <Wiersz nazwa="Utworzono">{formatujCzas(w.utworzono)}</Wiersz>
        {w.rozpoczeto && <Wiersz nazwa="Wysłano">{formatujCzas(w.rozpoczeto)}</Wiersz>}
        <Wiersz nazwa="Czas odpowiedzi">{formatujMs(w.czasMs)}</Wiersz>
        <Wiersz nazwa="Próba">
          {w.proba !== null ? `${w.proba}` : "-"}
          {w.ponowieniaWewnetrzne !== null && w.ponowieniaWewnetrzne > 0 && `, ponowień wewnątrz wywołania: ${w.ponowieniaWewnetrzne}`}
        </Wiersz>
        {w.numerWypowiedzi !== null && <Wiersz nazwa="Wypowiedź">numer {w.numerWypowiedzi}</Wiersz>}
        {w.operacjaId && (
          <Wiersz nazwa="Operacja">
            <Link to={`/developer?karta=wywolania&operacja=${encodeURIComponent(w.operacjaId)}`}>{w.operacjaId}</Link>
          </Wiersz>
        )}
        {w.sesja ? (
          <Wiersz nazwa="Analiza">
            <Link to={`/analizy/${encodeURIComponent(w.sesja.id)}`}>{w.sesja.tytul}</Link>
            {" - "}
            <Link to={`/developer?karta=wywolania&sesja=${encodeURIComponent(w.sesja.id)}`}>wszystkie wywołania tej analizy</Link>
          </Wiersz>
        ) : (
          w.sesjaId && (
            <Wiersz nazwa="Analiza">
              {w.sesjaId} (analizy już nie ma){" - "}
              <Link to={`/developer?karta=wywolania&sesja=${encodeURIComponent(w.sesjaId)}`}>wywołania tej analizy</Link>
            </Wiersz>
          )
        )}
        {w.probaMikrofonuId && <Wiersz nazwa="Próba mikrofonu">{w.probaMikrofonuId}</Wiersz>}
        {w.wersjaKodu && <Wiersz nazwa="Wersja aplikacji">{w.wersjaKodu}</Wiersz>}
        {w.endpoint && <Wiersz nazwa="Adres zapytania">{w.endpoint}</Wiersz>}
        <Wiersz nazwa="Identyfikator">{w.id}</Wiersz>
      </dl>
    </Card>
  );
}

function Zuzycie({ w }: { w: WywolanieSzczegolDto }): React.JSX.Element {
  const koszt = kosztWywolaniaDoPokazania(w.koszt, w.kurs);
  const liczba = (n: number | null): string | null => (n === null ? null : formatujLiczbe(n));
  return (
    <Card tytul="Zużycie i koszt">
      <div className="metryki">
        <div>
          <KpiWartosc
            etykieta="Tokeny wejścia"
            wartosc={liczba(w.tokenyWejscia)}
            druga={w.tokenyZCache !== null ? `w tym z cache: ${formatujLiczbe(w.tokenyZCache)}` : undefined}
            powodBraku="dostawca nie zwraca zużycia albo go nie zapisano"
          />
        </div>
        <div>
          <KpiWartosc
            etykieta="Tokeny wyjścia"
            wartosc={liczba(w.tokenyWyjscia)}
            druga={w.tokenyRozumowania !== null ? `w tym rozumowanie: ${formatujLiczbe(w.tokenyRozumowania)}` : undefined}
            powodBraku="dostawca nie zwraca zużycia albo go nie zapisano"
          />
        </div>
        <div>
          <KpiWartosc
            etykieta="Koszt (szacunek)"
            wartosc={koszt.usd}
            druga={koszt.pln ?? undefined}
            powodBraku={koszt.usd === null ? (koszt.powod ?? undefined) : undefined}
          />
        </div>
      </div>
      <p className="developer-meta">
        Szacunek wg cennika, nie faktura.{" "}
        {koszt.usd !== null && koszt.powod !== null && `Kwota jest dolnym ograniczeniem: ${koszt.powod}. `}
        {w.tokenyZCache !== null
          ? "Tokeny z cache są policzone tańszą stawką, o ile cennik ją zna. "
          : "Brak informacji o tokenach z cache (starszy wiersz albo dostawca jej nie zwraca): wejście liczone pełną stawką, więc kwota może być zawyżona. "}
        Nieudana próba bez zapisanego zużycia jest poza kosztem.
      </p>
    </Card>
  );
}

function Szczegol({ w, powrotSearch }: { w: WywolanieSzczegolDto; powrotSearch: string }): React.JSX.Element {
  const status = etykietaStatusu(w.status);
  return (
    <>
      <h1 className="page-title">
        Wywołanie {skrocId(w.id)} <Badge wariant={status.wariant}>{status.etykieta}</Badge>
      </h1>
      <p className="page-lead">
        {etykietaEtapu(w.zakres)}, {etykietaDostawcy(w.dostawca)} {w.model}, {formatujCzas(w.utworzono)}.
      </p>

      <Metadane w={w} />
      <Zuzycie w={w} />

      {w.blad && (
        <Card tytul="Błąd">
          <pre className="blok-tresci blok-tresci--krotki">{w.blad}</pre>
        </Card>
      )}

      {w.powiazane.length > 0 && (
        <Card tytul="Inne próby tej operacji">
          <TabelaWywolan wiersze={w.powiazane} kurs={w.kurs} powrotSearch={powrotSearch} />
        </Card>
      )}

      {w.parametry !== null && (
        <Card tytul="Parametry zapytania">
          <p className="developer-meta">Treść zadania bez nagrania i bez polecenia (te są niżej). Sekrety są wycięte.</p>
          <pre className="blok-tresci blok-tresci--krotki">{JSON.stringify(w.parametry, null, 2)}</pre>
          {w.naglowki && (
            <>
              <p className="developer-meta">Wybrane nagłówki odpowiedzi:</p>
              <pre className="blok-tresci blok-tresci--krotki">{JSON.stringify(w.naglowki, null, 2)}</pre>
            </>
          )}
        </Card>
      )}

      {w.audio && (
        <Card tytul="Nagranie wysłane do modelu">
          <dl className="developer-lista">
            {w.audio.bajty !== null && <Wiersz nazwa="Rozmiar">{formatujLiczbe(w.audio.bajty)} B</Wiersz>}
            {w.audio.czasMs !== null && <Wiersz nazwa="Długość">{formatujMs(w.audio.czasMs)}</Wiersz>}
            {w.audio.sha256 && <Wiersz nazwa="SHA-256">{w.audio.sha256}</Wiersz>}
            {w.audio.zrodloSha256 && <Wiersz nazwa="SHA-256 źródła">{w.audio.zrodloSha256}</Wiersz>}
            {w.audio.wycinekOdMs !== null && w.audio.wycinekDoMs !== null && (
              <Wiersz nazwa="Wycinek">
                od {formatujMs(w.audio.wycinekOdMs)} do {formatujMs(w.audio.wycinekDoMs)}
              </Wiersz>
            )}
          </dl>
          <p className="developer-meta">Samo nagranie nie jest tu przechowywane drugi raz, zostaje suma, rozmiar i zakres.</p>
        </Card>
      )}

      <TrescWywolania id={w.id} czesc="zapytanie" tytul="Zapytanie (polecenie)" stan={w.zapytanie} />
      <TrescWywolania id={w.id} czesc="odpowiedz" tytul="Odpowiedź" stan={w.odpowiedz} />
    </>
  );
}

/** Query string listy, z ktorej wchodzimy w szczegol (location.state od linku w tabeli). Obcy ksztalt state to pusty napis. */
function powrotSearchZeStanu(state: unknown): string {
  const wartosc = (state as { powrotSearch?: unknown } | null)?.powrotSearch;
  return typeof wartosc === "string" && (wartosc === "" || wartosc.startsWith("?")) ? wartosc : "";
}

export function WywolaniePage(): React.JSX.Element {
  const { id = "" } = useParams();
  const powrotSearch = powrotSearchZeStanu(useLocation().state);
  const { dane, kluczDanych, ladowanie, blad, statusBledu, odswiez } = useZasob(id, () => api.developerWywolanie(id));
  const powrot = (
    <p className="developer-powrot">
      <Link to={{ pathname: "/developer", search: powrotSearch }}>Wróć do historii wywołań</Link>
    </p>
  );

  // Dane innego wywolania (poprzedni adres) nie moga wisiec pod nowym: pokazujemy tylko odpowiedz zgodna z adresem.
  const wywolanie = dane !== null && kluczDanych === id ? dane : null;

  if (wywolanie === null && blad !== null && !ladowanie) {
    if (statusBledu === 404) {
      return (
        <>
          <h1 className="page-title">Wywołanie</h1>
          <Card>
            <StanPusty
              tytul="Nie ma takiego wywołania"
              opis="Identyfikator może być błędny albo wiersz został usunięty razem z analizą."
              akcja={<Link to="/developer">Wróć do historii wywołań</Link>}
            />
          </Card>
        </>
      );
    }
    return (
      <>
        <h1 className="page-title">Wywołanie</h1>
        <Card>
          <StanBledu
            tytul="Nie udało się wczytać wywołania"
            opis={komunikatBleduApi(blad)}
            akcja={<Button onClick={odswiez}>Spróbuj ponownie</Button>}
          />
        </Card>
        {powrot}
      </>
    );
  }

  if (wywolanie === null) {
    return (
      <>
        <h1 className="page-title">Wywołanie</h1>
        <Card>
          <StanLadowania tekst="Wczytuję wywołanie..." />
        </Card>
      </>
    );
  }

  return (
    <>
      <Szczegol w={wywolanie} powrotSearch={powrotSearch} />
      {powrot}
    </>
  );
}
